'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Props for `ResourceList`.
 */
export type ResourceListProps = SliceComponentProps<Content.ResourceListSlice>;

/**
 * Component for "ResourceList" Slices.
 */
const ResourceList: FC<ResourceListProps> = ({ slice }) => {
  const { section_title, resources } = slice.primary;

  const titleRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const titleElement = titleRef.current;
    const cardsElement = cardsContainerRef.current;

    if (titleElement) {
      titleElement.style.transitionDelay = '0ms';
      observer.observe(titleElement);
    }
    if (cardsElement) {
      cardsElement.style.transitionDelay = '150ms';
      observer.observe(cardsElement);
    }

    return () => {
      if (titleElement) {
        observer.unobserve(titleElement);
      }
      if (cardsElement) {
        observer.unobserve(cardsElement);
      }
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0e1110] w-full px-4 md:px-8 lg:px-[200px] py-[80px]"
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-6">
        {/* Section Title */}
        {isFilled.richText(section_title) && (
          <div
            ref={titleRef}
            className="font-medium text-[24px] md:text-[30px] leading-[1.2] text-white text-center capitalize w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            <PrismicRichText field={section_title} />
          </div>
        )}

        {/* Resource Cards */}
        <div
          ref={cardsContainerRef}
          className="flex flex-col gap-6 items-center w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {resources?.map((resource, index) => (
            <div
              key={index}
              className="relative flex flex-col md:flex-row items-start w-full rounded-[24px] overflow-hidden"
              style={{
                background:
                  'radial-gradient(1786.22% 95.9% at 95.9% 0%, rgba(255, 255, 255, 0.08) 0%, rgba(3, 191, 158, 0.15) 46.67%, rgba(255, 255, 255, 0.15) 100%)',
              }}
            >
              {/* Image */}
              {isFilled.image(resource.image) && (
                <div className="relative w-full md:w-[331px] h-[188px] shrink-0 overflow-hidden">
                  <PrismicNextImage
                    field={resource.image}
                    className="absolute inset-0 w-full h-full object-cover"
                    fill
                  />
                </div>
              )}

              {/* Content */}
              <div className="flex flex-col gap-4 p-6 flex-1">
                {/* Title */}
                {isFilled.richText(resource.title) && (
                  <div className="font-semibold text-[18px] md:text-[20px] leading-[1.4] text-white">
                    <PrismicRichText field={resource.title} />
                  </div>
                )}

                {/* Description */}
                {isFilled.richText(resource.description) && (
                  <div className="font-normal text-[16px] md:text-[18px] leading-[1.4] text-white">
                    <PrismicRichText field={resource.description} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResourceList;
